/* eslint-disable @typescript-eslint/naming-convention */
import React, { FC, useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useRemoteComponent } from "@bookingcom/remote-component";
import { customGoal } from "@flights/et-universal";

// eslint-disable-next-line import/no-deprecated
import { useStore } from "../../store";
import useRouteName from "../../hooks/useRouteName";
import useEvents from "../../hooks/useEvents";
import useClientMetrics from "../../hooks/useClientMetrics";

const REMOTE_COMPONENT_PATH_XM_FLIGHTS = "/SurveyTracker/Flights/Universal";

const SURVEY_CONTAINER_ID_PREFIX = "FlightsSurveyContainer";

enum XM_PAGE_NAME {
  FlightsCheckoutPage = "FlightsCheckoutPage",
  FlightsConfirmationPage = "FlightsConfirmationPage",
  FlightsBookingDetailsPage = "FlightsBookingDetailsPage",
  FlightsDetailsPage = "FlightsDetailsPage",
  PageClosed = "PageClosed"
}

const XM_EVENTS = {
  CLIENT_SIDE_SQUEAK: "CAPLA_EVENT_survey-tracker-component-service_CLIENT_SIDE_SQUEAK",
  REMOTE_COMPONENT_ORDER_INFO: "CAPLA_EVENT_xm_remote_component_order_info",
  XM_COMPONENT_INIT: "CAPLA_EVENT_xm_component_init",
  ET_CUSTOM_GOAL: "CAPLA_EVENT_et_custom_goal",
  FLIGHTS_SLOT_INFO: "CAPLA_EVENT_flights_slot_info",
  XM_REMOTE_LOADER_INIT: "CAPLA_EVENT_xm_remote_loader_init"
};

interface XMSurveyConfig {
  XMPageName?: string;
  containerId?: string;
}

const XM_SURVEY_CONFIG_BY_ROUTENAME: Record<string, XMSurveyConfig> = {
  confirmation: {
    XMPageName: XM_PAGE_NAME.FlightsConfirmationPage,
    containerId: `${SURVEY_CONTAINER_ID_PREFIX}-confirmation`
  },
  "pb-order-details": {
    XMPageName: XM_PAGE_NAME.FlightsBookingDetailsPage,
    containerId: `${SURVEY_CONTAINER_ID_PREFIX}-pb-order-details`
  },
  "checkout-payment": {
    XMPageName: XM_PAGE_NAME.FlightsCheckoutPage,
    containerId: `${SURVEY_CONTAINER_ID_PREFIX}-checkout-payment`
  },
  /* preload flight details remote component */
  searchresults: {},
  "searchresults-inbound": {},
  "searchresults-outbound": {},
  /* preload flight details remote component */
  flightdetails: {
    XMPageName: XM_PAGE_NAME.FlightsDetailsPage,
    containerId: `${SURVEY_CONTAINER_ID_PREFIX}-flight-details`
  },
  "flightdetails-inbound": {
    XMPageName: XM_PAGE_NAME.FlightsDetailsPage,
    containerId: `${SURVEY_CONTAINER_ID_PREFIX}-flight-details`
  },
  "flightdetails-outbound": {
    XMPageName: XM_PAGE_NAME.FlightsDetailsPage,
    containerId: `${SURVEY_CONTAINER_ID_PREFIX}-flight-details`
  }
};

export const SurveyTrackerFlights: FC<{ rc: React.ComponentType; error?: Error }> = ({ rc: Component, error }) => {
  const routeName = useRouteName();
  // eslint-disable-next-line import/no-deprecated
  const store = useStore();
  const { order } = store.order;
  const findContainerTimer = useRef(0); // find survey dom container on current webpage
  const containerDOMRef = useRef<HTMLElement | null>(null);
  const [containerFound, setContainerFound] = useState(false);

  const { grumble, squeak } = useEvents();
  const { trackClientMetric } = useClientMetrics();

  // set page name to control show/hidden
  useEffect(() => {
    const sendPageNameInfo = () => {
      if (routeName && containerFound) {
        document.dispatchEvent(
          new CustomEvent(XM_EVENTS.FLIGHTS_SLOT_INFO, {
            detail: {
              page: XM_SURVEY_CONFIG_BY_ROUTENAME[routeName].XMPageName || "",
              region: containerDOMRef?.current?.getAttribute("data-region")
            }
          })
        );
      }
    };
    sendPageNameInfo();
    document.addEventListener(XM_EVENTS.XM_REMOTE_LOADER_INIT, sendPageNameInfo);
    return () => {
      // sent a event for page closed
      document.dispatchEvent(
        new CustomEvent(XM_EVENTS.FLIGHTS_SLOT_INFO, {
          detail: { page: XM_PAGE_NAME.PageClosed }
        })
      );
      document.removeEventListener(XM_EVENTS.XM_REMOTE_LOADER_INIT, sendPageNameInfo);
    };
  }, [routeName, containerFound]);

  // Log
  useEffect(() => {
    const xmSqueakLogs = (e: any) => {
      try {
        const data = e.detail;
        if (data) {
          let squeakString = "xm";
          data.platform && (squeakString += `_${data.platform}`);
          data.surveyName && (squeakString += `_${data.surveyName}`);
          data.action && (squeakString += `_${data.action}`);
          squeak(squeakString);
          trackClientMetric(squeakString.toLowerCase() as any);
        }
        // eslint-disable-next-line flights/no-empty-catch
      } catch (e) {}
    };
    document.addEventListener(XM_EVENTS.CLIENT_SIDE_SQUEAK, xmSqueakLogs);
    return () => {
      document.removeEventListener(XM_EVENTS.CLIENT_SIDE_SQUEAK, xmSqueakLogs);
    };
  }, [squeak, trackClientMetric]);

  // communicate with RemoteComponent via CustomEvent
  useEffect(() => {
    const sendOrderInfo = () => {
      if (order) {
        const { orderStatus, orderId } = order;
        document.dispatchEvent(
          new CustomEvent(XM_EVENTS.REMOTE_COMPONENT_ORDER_INFO, { detail: { orderId, orderStatus } })
        );
      }
    };
    sendOrderInfo();
    document.addEventListener(XM_EVENTS.XM_COMPONENT_INIT, sendOrderInfo);
    return () => {
      document.removeEventListener(XM_EVENTS.XM_COMPONENT_INIT, sendOrderInfo);
    };
  }, [order]);

  // ET custom goal
  useEffect(() => {
    const xmETCustomGoal = (e: any) => {
      const data = e.detail;
      if (data && data.experimentTag && data.customGoal) {
        customGoal(data.experimentTag, data.customGoal);
      }
    };
    document.addEventListener(XM_EVENTS.ET_CUSTOM_GOAL, xmETCustomGoal);
    return () => {
      document.removeEventListener(XM_EVENTS.ET_CUSTOM_GOAL, xmETCustomGoal);
    };
  }, []);

  useEffect(() => {
    if (error) {
      grumble(" RC error: Survey tracker wrapper", { routeName, error: error.toString() });
    }
  }, [error, grumble, routeName]);

  const getContainerDom = () => {
    const id = XM_SURVEY_CONFIG_BY_ROUTENAME[routeName]?.containerId;
    if (!id) return;
    const elm = window?.document?.getElementById(id);
    if (elm) {
      window?.clearInterval(findContainerTimer.current);
      containerDOMRef.current = elm;
      findContainerTimer.current = 0;
      setContainerFound(true);
    }
    return elm;
  };

  useEffect(() => {
    setContainerFound(false);
    if (routeName in XM_SURVEY_CONFIG_BY_ROUTENAME) {
      findContainerTimer.current = window.setInterval(getContainerDom, 200);
    }
    return () => {
      findContainerTimer.current && window.clearInterval(findContainerTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeName]);

  const survey = useMemo(() => {
    if (containerFound && containerDOMRef.current) {
      return createPortal(<Component />, containerDOMRef.current);
    }
    return (
      <div id="xm-rc-placeholder" style={{ height: 0, visibility: "hidden" }}>
        <Component />
      </div>
    );
  }, [containerFound, Component]);

  return survey;
};

const SurveyTrackerFlightsUniversal: FC = () => {
  const routeName = useRouteName();
  if (routeName in XM_SURVEY_CONFIG_BY_ROUTENAME) {
    return <SurveyTrackerFlightsWithLoader />;
  }
  return null;
};

const SurveyTrackerFlightsWithLoader: FC = () => {
  const { Component, error } = useRemoteComponent({
    project: "web-shell",
    service: "header-mfe",
    path: REMOTE_COMPONENT_PATH_XM_FLIGHTS,
    type: "capla"
  });
  return <SurveyTrackerFlights rc={Component} error={error} />;
};

// if there will be multiple survey tracker instances on a single webpage, forceId is needed
export const SurveyTrackerLoader: FC<{ region?: string; forceId?: string }> = (props) => {
  const routeName = useRouteName();
  if (routeName in XM_SURVEY_CONFIG_BY_ROUTENAME) {
    return (
      <div id={props.forceId || XM_SURVEY_CONFIG_BY_ROUTENAME[routeName].containerId} data-region={props.region}></div>
    );
  }
  return null;
};

export default SurveyTrackerFlightsUniversal;
